/**
 * Contexte permissions — récupère et expose les permissions effectives du user courant.
 *
 * Usage :
 *   const { can, hasFlag, level } = usePermissions();
 *   if (!can('prospects', 'read')) return null;
 *   if (can('prospects', 'write')) <Button>Créer</Button>
 *
 * Composants utilitaires :
 *   <RequirePerm module="x" level="read"> ... </RequirePerm>   // masque si pas le droit
 *   <WriteOnly module="x"> ... </WriteOnly>                    // masque si pas write
 */
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const PermissionsContext = createContext(null);

const LEVELS = { none: 0, read: 1, write: 2 };

export function PermissionsProvider({ children }) {
    const { user } = useAuth();
    const [permissions, setPermissions] = useState({});
    const [flags, setFlags] = useState({});
    const [role, setRole] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        if (!user || typeof user !== 'object') {
            setPermissions({});
            setFlags({});
            setRole(null);
            setLoading(false);
            return;
        }
        try {
            const { data } = await axios.get(`${API}/api/me/permissions`, { withCredentials: true });
            setPermissions(data.permissions || {});
            setFlags(data.flags || {});
            setRole(data.role || null);
        } catch {
            // En cas d'erreur, on suppose pas de permissions (sécurisé)
            setPermissions({});
            setFlags({});
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const level = useCallback((moduleKey) => {
        if (role === 'admin') return 'write'; // admin toujours full
        return permissions[moduleKey] || 'none';
    }, [permissions, role]);

    const can = useCallback((moduleKey, requiredLevel = 'read') => {
        if (role === 'admin') return true;
        const userLevel = LEVELS[permissions[moduleKey] || 'none'];
        const need = LEVELS[requiredLevel] ?? 0;
        return userLevel >= need;
    }, [permissions, role]);

    const hasFlag = useCallback((flagKey) => {
        if (role === 'admin') {
            // Pour l'admin, les flags par défaut admin sont pris en compte
            return !!flags[flagKey];
        }
        return !!flags[flagKey];
    }, [flags, role]);

    return (
        <PermissionsContext.Provider value={{ permissions, flags, role, loading, can, level, hasFlag, refresh }}>
            {children}
        </PermissionsContext.Provider>
    );
}

export function usePermissions() {
    const ctx = useContext(PermissionsContext);
    if (!ctx) throw new Error('usePermissions must be used within PermissionsProvider');
    return ctx;
}

/** Masque enfant si l'utilisateur n'a pas le niveau requis. */
export function RequirePerm({ module: moduleKey, level = 'read', children, fallback = null }) {
    const { can } = usePermissions();
    return can(moduleKey, level) ? <>{children}</> : <>{fallback}</>;
}

/** Affiche enfant uniquement si l'utilisateur a le niveau write. */
export function WriteOnly({ module: moduleKey, children, fallback = null }) {
    const { can } = usePermissions();
    return can(moduleKey, 'write') ? <>{children}</> : <>{fallback}</>;
}

/** Désactive un enfant cliquable si pas write (clone Button/Link). */
export function DisableUnlessWrite({ module: moduleKey, children }) {
    const { can } = usePermissions();
    if (can(moduleKey, 'write')) return children;
    if (!children) return null;
    // Clone avec disabled=true
    return Array.isArray(children)
        ? children.map((c, i) => c?.type ? { ...c, props: { ...c.props, disabled: true, key: i } } : c)
        : { ...children, props: { ...children.props, disabled: true } };
}
